import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui-bits";

export default function SubstitutionSettingsPanel() {
  const [materialCategories, setMaterialCategories] = useState([]);
  const [allowed, setAllowed] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // The chip set is the union of (a) categories that actually appear on some
  // material AND (b) categories already on the saved allow-list. That way an
  // admin can still see and toggle off a category they added via the draft
  // input before any material uses it.
  const chipCategories = useMemo(() => {
    const set = new Set();
    materialCategories.forEach((c) => set.add(c));
    allowed.forEach((c) => set.add(c));
    return Array.from(set).sort();
  }, [materialCategories, allowed]);

  const load = async () => {
    setLoading(true);
    try {
      const [mats, settings] = await Promise.all([
        api.get("/materials"),
        api.get("/settings/substitution-categories"),
      ]);
      const cats = new Set();
      (mats.data || []).forEach((m) => {
        const c = String(m.category || "").trim().toLowerCase();
        if (c) cats.add(c);
      });
      setMaterialCategories(Array.from(cats).sort());
      setAllowed(
        (settings.data?.categories || []).map((c) =>
          String(c).trim().toLowerCase()
        )
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const isOn = (c) => allowed.includes(c);

  const toggle = (c) => {
    setAllowed((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]
    );
  };

  const addDraft = () => {
    const c = draft.trim().toLowerCase();
    if (!c) return;
    if (!allowed.includes(c)) setAllowed((a) => [...a, c]);
    setDraft("");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/substitution-categories", {
        categories: allowed,
      });
      toast.success("Substitution categories saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-10 text-center text-stone-500">Loading settings…</div>;
  }

  return (
    <Panel title="Ingredient category substitution">
      <div className="space-y-5">
        <p className="text-sm text-stone-600 dark:text-stone-300">
          When an operator scans an ingredient at batch time that doesn&apos;t
          match the recipe slot exactly, the app will allow it as a
          substitution if both materials share a <em>category</em> (set on
          the Raw Materials page) <strong>and</strong> that category is
          enabled below. Every substitution is recorded in the Audit Log and
          flagged on the batch&apos;s consumption record.
        </p>

        <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 p-3 text-xs text-amber-900 dark:text-amber-200">
          Tip: <strong>only enable categories where any brand is genuinely
          interchangeable.</strong> Leave allergen-bearing categories
          (e.g. nuts) off so the system enforces the exact recipe slot.
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider font-semibold">
            Categories used by your materials
          </Label>
          <div
            className="mt-2 flex flex-wrap gap-2"
            data-testid="substitution-categories"
          >
            {chipCategories.length === 0 && (
              <span className="text-sm text-stone-500">
                No categories yet — add one on a material first, or below.
              </span>
            )}
            {chipCategories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => toggle(c)}
                aria-pressed={isOn(c)}
                className={`px-3 h-9 rounded-full border text-sm font-medium transition-colors ${
                  isOn(c)
                    ? "bg-emerald-100 border-emerald-400 text-emerald-900"
                    : "bg-white border-stone-300 hover:bg-stone-50 dark:bg-stone-900 dark:border-stone-700"
                }`}
                data-testid={`subcat-toggle-${c}`}
              >
                {isOn(c) ? "✓ " : ""}
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-xs uppercase tracking-wider font-semibold">
              Add a new category
            </Label>
            <Input
              className="h-11 mt-1"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="e.g. flour"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addDraft();
                }
              }}
              data-testid="subcat-draft"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            className="h-11"
            onClick={addDraft}
            disabled={!draft.trim()}
            data-testid="subcat-add"
          >
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>

        <div className="flex justify-end">
          <Button
            type="button"
            onClick={save}
            disabled={saving}
            className="h-11 px-6"
            data-testid="subcat-save"
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </Panel>
  );
}
